import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Bell, Plus, Trash2, TrendingUp, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { motion, AnimatePresence } from 'framer-motion';
import { MAINTENANCE_MODE } from '@/App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AlertsPanel = ({ currentPrice }) => {
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);
  const [priceTarget, setPriceTarget] = useState('');
  const [condition, setCondition] = useState('above');
  const [adding, setAdding] = useState(false);

  const fetchAlerts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/alerts`);
      setAlerts(response.data);
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const addAlert = async () => {
    if (MAINTENANCE_MODE) {
      toast.error('Alerts are temporarily disabled during maintenance.');
      return;
    }
    
    if (!priceTarget) {
      toast.error('Please enter a price target');
      return;
    }

    setAdding(true);
    try {
      await axios.post(`${API}/alerts`, {
        price_target: parseFloat(priceTarget),
        condition
      });
      toast.success('Alert created!');
      setPriceTarget('');
      setCondition('above');
      setOpen(false);
      fetchAlerts();
    } catch (error) {
      console.error('Error adding alert:', error);
      toast.error('Failed to create alert');
    } finally {
      setAdding(false);
    }
  };

  const deleteAlert = async (id) => {
    try {
      await axios.delete(`${API}/alerts/${id}`);
      toast.success('Alert deleted');
      fetchAlerts();
    } catch (error) {
      console.error('Error deleting alert:', error);
      toast.error('Failed to delete alert');
    }
  };

  useEffect(() => {
    if (!currentPrice || alerts.length === 0) return;
    
    alerts.forEach(alert => {
      if (!alert.active) return;
      
      if (alert.condition === 'above' && currentPrice >= alert.price_target) {
        toast.success('Price Alert Triggered!', {
          description: `Bitcoin is now above $${alert.price_target.toLocaleString()}`
        });
      } else if (alert.condition === 'below' && currentPrice <= alert.price_target) {
        toast.warning('Price Alert Triggered!', {
          description: `Bitcoin is now below $${alert.price_target.toLocaleString()}`
        });
      }
    });
  }, [currentPrice, alerts]);

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-blue-500" />
          <h3 className="text-xl font-heading font-semibold tracking-tight">Price Alerts</h3>
        </div>
        <Dialog open={open} onOpenChange={(v) => !MAINTENANCE_MODE && setOpen(v)}>
          <DialogTrigger asChild>
            <Button 
              data-testid="add-alert-btn" 
              size="sm" 
              disabled={MAINTENANCE_MODE}
              className="bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4 mr-1" />
              {MAINTENANCE_MODE ? 'Unavailable' : 'Add Alert'}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Price Alert</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4" aria-describedby="create-alert-description">
              <p id="create-alert-description" className="sr-only">Set a target price and condition to receive an alert when Bitcoin reaches that price</p>
              <div>
                <label className="text-sm font-medium mb-2 block">Target Price (USD)</label>
                <Input
                  data-testid="alert-price-input"
                  type="number"
                  step="100"
                  placeholder="100000"
                  value={priceTarget}
                  onChange={(e) => setPriceTarget(e.target.value)}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Condition</label>
                <Select value={condition} onValueChange={setCondition}>
                  <SelectTrigger data-testid="alert-condition-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="above">Price goes above</SelectItem>
                    <SelectItem value="below">Price goes below</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                data-testid="submit-alert-btn"
                onClick={addAlert}
                disabled={adding}
                className="w-full"
              >
                {adding ? 'Creating...' : 'Create Alert'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <AnimatePresence>
          {alerts.length === 0 ? (
            <div className="col-span-full flex flex-col items-center justify-center text-center py-8">
              <Bell className="w-12 h-12 text-muted-foreground/50 mb-3" />
              <p className="text-sm text-muted-foreground">No alerts set</p>
              <p className="text-xs text-muted-foreground mt-1">Create alerts to track price movements</p>
            </div>
          ) : (
            alerts.map((alert, index) => (
              <motion.div
                key={alert.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ delay: index * 0.05 }}
                className={`p-4 rounded-xl border-2 ${
                  alert.condition === 'above'
                    ? 'border-gain/30 bg-gain-soft/20'
                    : 'border-loss/30 bg-loss-soft/20'
                }`}
                data-testid={`alert-${alert.id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {alert.condition === 'above' ? (
                      <TrendingUp className="w-4 h-4 text-gain" />
                    ) : (
                      <TrendingDown className="w-4 h-4 text-loss" />
                    )}
                    <span className="font-medium text-sm capitalize">{alert.condition}</span>
                  </div>
                  <Button
                    data-testid={`delete-alert-${alert.id}`}
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteAlert(alert.id)}
                    className="h-6 w-6 text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
                <p className="text-2xl font-bold font-heading">
                  ${alert.price_target.toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {alert.active ? 'Active' : 'Inactive'}
                </p>
              </motion.div>
            ))
          )}
        </AnimatePresence>
      </div>
    </Card>
  );
};

export default AlertsPanel;